"use client";

import React from "react";
import { ExternalLink, FileText } from "lucide-react";
import { extractVideoId } from "@/lib/youtube";

/** URL regex — matches http(s):// or bare www. URLs */
const URL_REGEX = /(?:https?:\/\/|www\.)\S+[^\s.,;:!?)"'\]]/gi;

/** Image extensions that can render inline */
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?.*)?$/i;

/** App-internal path patterns — navigate in same window, not a new tab */
const INTERNAL_PATHS = /\/(party|jam-session|music|artists|marketplace|news|videos|profile|playlist)(\/|$|\?)/i;

const isYouTubeUrl   = (u: string) => /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)/i.test(u);
const isSpotifyUrl   = (u: string) => /open\.spotify\.com\/(track|album|playlist|episode)\//i.test(u);
const isSoundCloudUrl= (u: string) => /soundcloud\.com\//i.test(u);
const isMixcloudUrl  = (u: string) => /mixcloud\.com\//i.test(u);
const isImageUrl     = (u: string) => IMAGE_EXT.test(u.split('?')[0]);
const isPdfUrl       = (u: string) => /\.pdf(\?.*)?$/i.test(u.split('?')[0]);

/** True when the URL points to our own app (same origin OR known production domains). */
function isInternalAppUrl(url: string): boolean {
  try {
    const { hostname, pathname } = new URL(url);
    const appOrigin = typeof window !== 'undefined' ? window.location.hostname : '';
    const knownDomains = ['breakinbread.com', 'www.breakinbread.com', 'bb-native-one.vercel.app'];
    if (hostname !== appOrigin && !knownDomains.includes(hostname)) return false;
    return INTERNAL_PATHS.test(pathname);
  } catch {
    return false;
  }
}

/** Extract just the path+query from a full URL so router.push can use it */
function toInternalPath(url: string): string {
  try {
    const { pathname, search } = new URL(url);
    return pathname + search;
  } catch {
    return url;
  }
}

function normalise(url: string): string {
  return url.startsWith("http") ? url : `https://${url}`;
}

function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function spotifyEmbedUrl(url: string): string | null {
  const m = url.match(/open\.spotify\.com\/(track|album|playlist|episode)\/([A-Za-z0-9]+)/i);
  return m ? `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator&theme=0` : null;
}

/** Extract Mixcloud feed path from a profile/show URL */
function mixcloudEmbedUrl(url: string): string | null {
  // e.g. https://www.mixcloud.com/ArtistName/show-name/
  const m = url.match(/mixcloud\.com(\/[^?#]+)/i);
  if (!m) return null;
  return `https://www.mixcloud.com/widget/iframe/?hide_cover=1&feed=${encodeURIComponent(m[1])}`;
}

// ── per-URL embed renderer ────────────────────────────────────────────────────

function LinkEmbed({ url }: { url: string }) {
  const href = normalise(url);

  if (isYouTubeUrl(href)) {
    const yid = extractVideoId(href);
    if (yid) return (
      <div className="mt-2 rounded-lg overflow-hidden border border-border w-full max-w-xs aspect-video">
        <iframe
          src={`https://www.youtube.com/embed/${yid}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen className="w-full h-full" title="YouTube video"
        />
      </div>
    );
  }

  if (isSpotifyUrl(href)) {
    const embedUrl = spotifyEmbedUrl(href);
    if (embedUrl) return (
      <div className="mt-2 rounded-xl overflow-hidden w-full max-w-xs">
        <iframe src={embedUrl}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy" className="w-full" style={{ height: 80, border: "none" }} title="Spotify player"
        />
      </div>
    );
  }

  if (isSoundCloudUrl(href)) return (
    <div className="mt-2 rounded-xl overflow-hidden w-full max-w-xs">
      <iframe scrolling="no" allow="autoplay"
        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(href)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
        className="w-full" style={{ height: 80, border: "none" }} title="SoundCloud player"
      />
    </div>
  );

  if (isMixcloudUrl(href)) {
    const embedUrl = mixcloudEmbedUrl(href);
    if (embedUrl) return (
      <div className="mt-2 rounded-xl overflow-hidden w-full max-w-xs">
        <iframe
          src={embedUrl} allow="autoplay"
          className="w-full" style={{ height: 120, border: "none" }} title="Mixcloud player"
        />
      </div>
    );
  }

  if (isImageUrl(href)) return (
    // eslint-disable-next-line @next/next/no-img-element
    <a href={href} target="_blank" rel="noopener noreferrer" className="block mt-2">
      <img
        src={href} alt="shared image"
        className="rounded-lg max-w-xs max-h-64 object-cover border border-border"
        loading="lazy"
      />
    </a>
  );

  if (isPdfUrl(href)) return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-2 mt-2 px-3 py-2 rounded-lg border border-border bg-muted hover:bg-muted/80 transition-colors text-xs font-medium"
    >
      <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
      <span className="truncate max-w-[200px]">{displayUrl(url)}</span>
      <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
    </a>
  );

  // Generic clickable link chip
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 mt-1 text-xs text-primary underline underline-offset-2 break-all hover:opacity-80 transition-opacity"
    >
      <ExternalLink className="h-3 w-3 flex-shrink-0" />
      {displayUrl(url)}
    </a>
  );
}

// ── Inline markdown parser ────────────────────────────────────────────────────
// Handles the subset of markdown that Claude AI commonly outputs:
//   **bold**, *italic*, `code`, ~~strikethrough~~
// URLs inside inline spans are handled by the outer line-level parser.

function parseInline(raw: string, keyPrefix: string): React.ReactNode[] {
  // Match in order: **bold**, *italic*, `code`, ~~strike~~
  const INLINE = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|~~(.+?)~~)/g;
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let idx = 0;
  while ((m = INLINE.exec(raw)) !== null) {
    if (m.index > last) nodes.push(<React.Fragment key={`${keyPrefix}-t${idx++}`}>{raw.slice(last, m.index)}</React.Fragment>);
    if (m[2] !== undefined) nodes.push(<strong key={`${keyPrefix}-b${idx++}`} className="font-semibold">{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<em key={`${keyPrefix}-i${idx++}`} className="italic">{m[3]}</em>);
    else if (m[4] !== undefined) nodes.push(<code key={`${keyPrefix}-c${idx++}`} className="font-mono text-[0.85em] bg-black/10 dark:bg-white/10 px-1 py-0.5 rounded">{m[4]}</code>);
    else if (m[5] !== undefined) nodes.push(<s key={`${keyPrefix}-s${idx++}`} className="opacity-60">{m[5]}</s>);
    last = m.index + m[0].length;
  }
  if (last < raw.length) nodes.push(<React.Fragment key={`${keyPrefix}-t${idx++}`}>{raw.slice(last)}</React.Fragment>);
  return nodes;
}

// ── Line-level renderer ───────────────────────────────────────────────────────
// Splits text into lines, detects markdown block-level constructs, then
// runs URL detection + inline markdown on each segment.

function renderLine(line: string, lineIdx: number, embedUrls: string[]): React.ReactNode {
  // Horizontal rule
  if (/^[-*_]{3,}$/.test(line.trim())) {
    return <hr key={`hr-${lineIdx}`} className="border-t border-current opacity-20 my-1" />;
  }

  // Heading: # / ## / ###
  const headingMatch = line.match(/^(#{1,3})\s+(.+)/);
  if (headingMatch) {
    const level = headingMatch[1].length;
    const cls = level === 1
      ? "text-base font-bold mt-1"
      : level === 2
      ? "text-sm font-bold mt-0.5"
      : "text-sm font-semibold";
    return (
      <div key={`h-${lineIdx}`} className={cls}>
        {renderInlineWithUrls(headingMatch[2], `h${lineIdx}`, embedUrls)}
      </div>
    );
  }

  // Blockquote: > text
  const quoteMatch = line.match(/^>\s*(.*)/);
  if (quoteMatch) {
    return (
      <div key={`q-${lineIdx}`} className="border-l-2 border-current opacity-70 pl-2 italic text-sm">
        {renderInlineWithUrls(quoteMatch[1], `q${lineIdx}`, embedUrls)}
      </div>
    );
  }

  // Bullet list: - / * / • (not horizontal rule — already handled)
  const bulletMatch = line.match(/^[\-*•]\s+(.*)/);
  if (bulletMatch) {
    return (
      <div key={`li-${lineIdx}`} className="flex gap-1.5 items-start">
        <span className="mt-[3px] flex-shrink-0 w-1 h-1 rounded-full bg-current opacity-60 mt-[6px]" />
        <span>{renderInlineWithUrls(bulletMatch[1], `li${lineIdx}`, embedUrls)}</span>
      </div>
    );
  }

  // Numbered list: 1. / 2. etc.
  const numberedMatch = line.match(/^(\d+)\.\s+(.*)/);
  if (numberedMatch) {
    return (
      <div key={`nl-${lineIdx}`} className="flex gap-1.5 items-start">
        <span className="flex-shrink-0 text-[0.85em] opacity-70 font-mono">{numberedMatch[1]}.</span>
        <span>{renderInlineWithUrls(numberedMatch[2], `nl${lineIdx}`, embedUrls)}</span>
      </div>
    );
  }

  // Code block fence line (``` or ~~~) — treat as a literal line
  if (/^(`{3}|~{3})/.test(line)) {
    return null; // fences themselves are invisible
  }

  // Plain text — URL + inline markdown pass
  return (
    <span key={`p-${lineIdx}`}>
      {renderInlineWithUrls(line, `p${lineIdx}`, embedUrls)}
    </span>
  );
}

/** URL detection pass, then inline markdown within each text segment. */
function renderInlineWithUrls(raw: string, keyPrefix: string, embedUrls: string[]): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = new RegExp(URL_REGEX.source, "gi");
  let match: RegExpExecArray | null;
  let idx = 0;

  while ((match = regex.exec(raw)) !== null) {
    const url = match[0];
    const start = match.index;

    if (start > lastIndex) {
      const segment = raw.slice(lastIndex, start);
      nodes.push(...parseInline(segment, `${keyPrefix}-s${idx++}`));
    }

    const href = normalise(url);
    const internal = isInternalAppUrl(href);
    nodes.push(
      <a key={`${keyPrefix}-u${idx++}`}
        href={internal ? toInternalPath(href) : href}
        target={internal ? '_self' : '_blank'}
        rel={internal ? undefined : 'noopener noreferrer'}
        className="inline-flex items-center gap-1 text-primary underline underline-offset-2 break-all hover:opacity-80 transition-opacity"
      >
        <ExternalLink className="h-3 w-3 flex-shrink-0" />
        {displayUrl(url)}
      </a>
    );

    embedUrls.push(url);
    lastIndex = start + url.length;
  }

  if (lastIndex < raw.length) {
    nodes.push(...parseInline(raw.slice(lastIndex), `${keyPrefix}-s${idx++}`));
  }

  return nodes;
}

// ── main component ────────────────────────────────────────────────────────────

interface RichTextProps {
  text: string;
  className?: string;
}

/**
 * Renders plain text with embedded rich content:
 * - Markdown: **bold**, *italic*, `code`, ~~strike~~, # headers, - bullets, 1. numbered, > quote, ---
 * - URLs: YouTube player, Spotify widget, SoundCloud, Mixcloud, inline image, PDF chip, link chip
 * - Line breaks preserved
 */
export function RichText({ text, className }: RichTextProps) {
  if (!text) return null;

  const embedUrls: string[] = [];

  // Split into lines, render each
  const lines = text.split('\n');

  // Track whether we're inside a fenced code block
  let inCodeBlock = false;
  const codeBlockLines: string[] = [];
  const renderedBlocks: React.ReactNode[] = [];
  let codeKey = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isFence = /^(`{3}|~{3})/.test(line.trim());

    if (isFence && !inCodeBlock) {
      inCodeBlock = true;
      codeBlockLines.length = 0;
      continue;
    }
    if (isFence && inCodeBlock) {
      inCodeBlock = false;
      renderedBlocks.push(
        <pre key={`cb-${codeKey++}`}
          className="font-mono text-[0.82em] bg-black/10 dark:bg-white/10 rounded p-2 my-1 overflow-x-auto whitespace-pre">
          {codeBlockLines.join('\n')}
        </pre>
      );
      codeBlockLines.length = 0;
      continue;
    }
    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    const rendered = renderLine(line, i, embedUrls);
    if (rendered !== null) {
      renderedBlocks.push(rendered);
    }
    // Add line break between non-null lines (but not after last line)
    if (rendered !== null && i < lines.length - 1) {
      renderedBlocks.push(<br key={`br-${i}`} />);
    }
  }

  // Rich embeds appear below text
  const embeds = embedUrls
    .filter(u => {
      const h = normalise(u);
      return isYouTubeUrl(h) || isSpotifyUrl(h) || isSoundCloudUrl(h) ||
             isMixcloudUrl(h) || isImageUrl(h) || isPdfUrl(h);
    })
    // Deduplicate
    .filter((u, i, arr) => arr.indexOf(u) === i)
    .map((u, i) => <LinkEmbed key={`e-${i}`} url={u} />);

  return (
    <span className={className}>
      {renderedBlocks}
      {embeds.length > 0 && (
        <span className="flex flex-col gap-2 mt-1">{embeds}</span>
      )}
    </span>
  );
}
